import Utils from '../../../utils/utils';
import { isActivityVisible, isLocationVisible, isNumberOfGuestsRequired, shouldShowNumberOfGuests } from './conditional-fields';
import { isGuestCountValid } from './guest-count-validity';

const isBlank = (value) => value == null || String(value).trim() === '';

/**
 * @param {object} [options]
 * @param {Array<'loading'|'optional'|'required'|undefined>} [options.imputationLeafStages] — per expense index; when
 *   `optional` or `loading`, imputation leaf is not required (L1-only roots or fetch in flight).
 */
export const validateExpenseReviewForm = (data, mainInfo, expenses, options = {}) => {
  const { imputationLeafStages } = options;
  const isElite = Utils.isExportFormat(data, 'elite');
  const errors = [];

  if (Utils.shouldShowExpenseReviewInvoiceNumber(data)) {
    const requiredFields = Utils.getRequiredExpenseFields(data);
    const invoiceNumberRequired =
      requiredFields == null ||
      Utils.isExpenseFieldRequired(data, 'field_invoice_number', true);
    if (invoiceNumberRequired && !mainInfo.invoiceNumber) {
      errors.push(data.t.read('review_expense.error.invoice_number_required'));
    }
  }
  if (!mainInfo.date) {
    errors.push(data.t.read('review_expense.error.invoice_date_required'));
  }

  expenses.forEach((expense, index) => {
    const suffix = expenses.length > 1 ? ` ${index + 1}` : '';
    if (Array.isArray(expense.matterSplits)) {
      const allMattersSelected = expense.matterSplits.every((s) => s.matter);
      if (!allMattersSelected) {
        errors.push(data.t.read('review_expense.error.matter_split_all_required') + suffix);
      }
      const matterIds = expense.matterSplits.map((s) => s.matter?.value?.number ?? s.matter?.number ?? s.matter).filter(Boolean);
      const hasDuplicates = matterIds.length !== new Set(matterIds).size;
      if (hasDuplicates) {
        errors.push(data.t.read('review_expense.error.matter_split_duplicate') + suffix);
      }
      const percentageSum = expense.matterSplits.reduce((acc, s) => acc + (Number(s.percentage) || 0), 0);
      if (percentageSum !== 100) {
        errors.push(data.t.read('review_expense.error.matter_split_sum') + suffix);
      }
    } else if (Utils.isExpenseFieldRequired(data, 'field_matter') && !expense.matter && expense.expenseType !== 'internal') {
      errors.push(data.t.read('review_expense.error.matter_required') + suffix);
    }
    if (Utils.isExpenseFieldRequired(data, 'field_expense_date') && !expense.date) {
      errors.push(data.t.read('review_expense.error.date_required') + suffix);
    }
    if (Utils.isExpenseFieldRequired(data, 'field_cost_type') && !expense.costType) {
      errors.push(data.t.read('review_expense.error.cost_type_required') + suffix);
    }
    if (!expense.info.amount || isNaN(expense.info.amount) || expense.info.amount <= 0) {
      errors.push(data.t.read('review_expense.error.amount_required') + suffix);
    }
    if (Utils.isExpenseFieldRequired(data, 'field_supplier_event') && isBlank(expense.info.supplier_event)) {
      errors.push(data.t.read('review_expense.error.supplier_event_required') + suffix);
    }
    if (Utils.isExpenseFieldRequired(data, 'field_narrative') && isBlank(expense.info.narrative)) {
      errors.push(data.t.read('review_expense.error.narrative_required') + suffix);
    }
    if (Utils.isExpenseFieldRequired(data, 'field_tips') && isBlank(expense.info.tip)) {
      errors.push(data.t.read('review_expense.error.tip_required') + suffix);
    }
    if (Utils.isExpenseFieldRequired(data, 'field_tax_code') && isBlank(expense.info.taxCode)) {
      errors.push(data.t.read('review_expense.error.tax_code_required') + suffix);
    }
    
    if (shouldShowNumberOfGuests(data, expense.costType, expense.category, expense.imputationVerifyCategory)) {
      const numberOfGuests = expense.info.numberOfGuests;
      const guestsRequired = isNumberOfGuestsRequired(
        data,
        expense.costType,
        expense.category,
        expense.imputationVerifyCategory
      );
      if (!isGuestCountValid(numberOfGuests, guestsRequired)) {
        errors.push(data.t.read('review_expense.error.number_of_guests_required') + suffix);
      }
    }
    
    if (
      Utils.hasExpenseField(data, 'field_activity') &&
      Utils.isExpenseFieldRequired(data, 'field_activity') &&
      isActivityVisible(expense.matter, expense.isBillable !== false, isElite) &&
      !expense.activity
    ) {
      errors.push(data.t.read('review_expense.error.activity_required') + suffix);
    }

    if (
      Utils.isExpenseFieldRequired(data, 'field_imputation_level1') &&
      (!expense.imputationLevel1Id || String(expense.imputationLevel1Id).trim() === '')
    ) {
      errors.push(data.t.read('review_expense.error.imputation_level1_required') + suffix);
    }
    const hasL1 =
      expense.imputationLevel1Id && String(expense.imputationLevel1Id).trim() !== '';
    if (
      Utils.hasExpenseField(data, 'field_imputation_level1', false) &&
      hasL1 &&
      imputationLeafStages?.[index] === 'required' &&
      (!expense.imputationLeafId || String(expense.imputationLeafId).trim() === '')
    ) {
      errors.push(data.t.read('review_expense.error.imputation_leaf_required') + suffix);
    }
    
    // Validate location only when the field_location flag is on and the cost type requires it
    if (
      Utils.hasExpenseField(data, 'field_location', false)
      && isLocationVisible(
        expense.costType,
        expense.category,
        isElite,
        expense.imputationVerifyCategory,
      )
      && !expense.info?.location
    ) {
      errors.push(data.t.read('review_expense.error.location_required') + suffix);
    }
  });

  return errors;
};
